import { useCallback, useSyncExternalStore } from "react";
import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";

/** One place to build a GET query, so no screen hand-rolls a fetch. */
export function useApiQuery<T>(
  key: readonly unknown[],
  path: string,
  options?: Omit<
    UseQueryOptions<T, Error, T, readonly unknown[]>,
    "queryKey" | "queryFn"
  >,
) {
  const session = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  return useQuery<T, Error, T, readonly unknown[]>({
    queryKey: key,
    queryFn: ({ signal }) => apiFetch<T>(path, { signal }),
    ...options,
    enabled: session.changing ? false : options?.enabled,
  });
}

/**
 * A keyset-paged GET (P9.6): the first page at `path`, each next one at
 * `path?cursor=<the page before's nextCursor>`, until a page answers
 * `nextCursor: null`. The same session gate as `useApiQuery`.
 */
export function useApiCursorQuery<TPage extends { nextCursor: string | null }>(
  key: readonly unknown[],
  path: string,
  options?: { staleTime?: number },
) {
  const session = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ signal, pageParam }) =>
      apiFetch<TPage>(
        pageParam === null ? path : `${path}?cursor=${encodeURIComponent(pageParam)}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page: TPage) => page.nextCursor,
    staleTime: options?.staleTime,
    enabled: session.changing ? false : undefined,
  });
}

/** What `useObservedQueryData` answers. */
export type ObservedQueryData<T> =
  | { observed: false }
  | { observed: true; data: T | undefined };

/**
 * A query's data while something ON SCREEN reads it (an enabled observer;
 * this hook is none), else `observed: false` (P7). A slim read stands down while the
 * heavy one it summarises is already being read, and takes the figure off
 * that read instead — so the two never disagree and nothing is asked twice.
 * It watches the query cache rather than observing the query: an observer
 * here would itself keep the heavy read alive.
 */
export function useObservedQueryData<T>(
  key: readonly unknown[],
): ObservedQueryData<T> {
  const cache = useQueryClient().getQueryCache();
  const subscribe = useCallback(
    (onChange: () => void) => cache.subscribe(onChange),
    [cache],
  );
  const state = useSyncExternalStore(subscribe, () => {
    const query = cache.find({ queryKey: key, exact: true });
    // `isActive`: an ENABLED observer — one that is reading it, not one
    // switched off for this role or this moment.
    return query?.isActive() ? query.state : null;
  });
  return state ? { observed: true, data: state.data as T | undefined } : NOT_OBSERVED;
}

const NOT_OBSERVED = { observed: false } as const;

export const queryKeys = {
  session: ["session"] as const,
  grid: ["restaurant", "grid"] as const,
  /**
   * The live bills that sit at NO table (F1, unit 4).
   *
   * Its own key rather than a slice of `grid`, because it is its own read: the
   * floor grid is built from AREAS and a takeaway bill belongs to none, so a
   * bill the till just opened would otherwise have no screen at all to come
   * back to.
   */
  offPremiseOpen: ["restaurant", "off-premise-open"] as const,
  /** P5-Q — the floor's "show older" list; read only while it is open. */
  olderCheques: ["restaurant", "older-cheques"] as const,
  /**
   * The customer's REQUESTS for a bag (F24, §5.12) — the strip's list and the
   * badge's count. Two keys under one prefix: committed order revisions and
   * an accept or reject invalidate both through the prefix.
   */
  onlineOrders: ["restaurant", "online-orders"] as const,
  onlineOrdersRequested: ["restaurant", "online-orders", "requested"] as const,
  onlineOrdersCount: ["restaurant", "online-orders", "count"] as const,
  /**
   * Call waiter (D132): the store's open diner asks. One key for the floor's
   * badges, the bill's list and the chime, so the three share one read.
   */
  serviceRequests: ["restaurant", "service-requests"] as const,
  invoice: (id: string) => ["restaurant", "invoice", id] as const,
  /** T6 / F29 — keyed on the typed number's DIGITS, so spacing re-asks nothing. */
  customerSuggestion: (digits: string) =>
    ["restaurant", "customer-suggestion", digits] as const,
  menuItems: ["restaurant", "menu-items"] as const,
  /** B117/Q5 — the manager's remake report, keyed by the period it is over. */
  reportRemakes: (from: string, to: string) =>
    ["restaurant", "reports", "remakes", from, to] as const,
  /** B124/D114 — the service speed report, keyed by its period. */
  reportServiceSpeed: (from: string, to: string) =>
    ["restaurant", "reports", "service-speed", from, to] as const,
  /** Founder decision 5 — the refunds report, keyed by its period. */
  reportRefunds: (from: string, to: string) =>
    ["restaurant", "reports", "refunds", from, to] as const,
  /** D134 — diners' feedback, keyed by its period and page. */
  feedback: (from: string, to: string, page: number, pageSize: number) =>
    ["restaurant", "feedback", from, to, page, pageSize] as const,
  posPriceTiers: ["restaurant", "pos-price-tiers"] as const,
  recipe: (productId: string) =>
    ["restaurant", "menu-items", productId, "recipe"] as const,
  addonGroups: ["restaurant", "addon-groups"] as const,
  areas: ["restaurant", "areas"] as const,
  tables: ["restaurant", "tables"] as const,
  tiers: ["restaurant", "price-tiers"] as const,
  /** The gallery of themes this store may apply (already kind-filtered). */
  themeGallery: ["seller", "themes"] as const,
  /** The store's own theme row — live + draft. */
  storeTheme: ["seller", "theme"] as const,
  storeThemePreviewToken: ["seller", "theme", "preview-token"] as const,
  ingredients: ["restaurant", "ingredients"] as const,
  /**
   * One reconcile result per ingredient. Never folded into `ingredients`: it is
   * fetched on demand from a different endpoint and must not be swept away (or
   * refilled) by a purchase invalidating the list.
   */
  reconcile: (ingredientId: string) =>
    ["restaurant", "ingredients", ingredientId, "reconcile"] as const,
  /**
   * A page of invoice history, keyed by the FULL query string.
   *
   * Filters and page number are all in that string, so two different filter
   * sets can never share a cached page — which is what lets the pager keep the
   * previous page on screen while the next one loads without ever showing rows
   * from the wrong query.
   */
  invoiceHistory: (search: string) =>
    ["restaurant", "invoices", "history", search] as const,
  settings: ["restaurant", "settings"] as const,
  /**
   * The kitchen board, keyed by the station it is scoped to.
   *
   * The station is IN the key rather than a filter applied after the fetch:
   * `/restaurant/kitchen-board` is a different request per station, and sharing
   * one cache entry would flash the grill's plates onto the bar's screen for as
   * long as the refetch takes. `null` is the whole-kitchen board and is a key
   * of its own for the same reason.
   */
  kitchenBoard: (stationId: string | null) =>
    ["restaurant", "kitchen-board", stationId ?? "all"] as const,
  /**
   * P7.1 — the floor's Kitchen tab: the board's count, toggle and stations
   * without its plates. Under `kitchen-board` on purpose, so every signal and
   * invalidation that refreshes the board refreshes the tab with it.
   */
  kitchenBoardTab: ["restaurant", "kitchen-board", "tab"] as const,
  /**
   * P7.4 — the back office sidebar's two figures. Each sits under the list it
   * summarises, so the realtime signal and every prefix invalidation that
   * refreshed the list refreshes the figure; the ingredient list's EXACT
   * invalidations name the figure beside it.
   */
  menuItemsCount: ["restaurant", "menu-items", "count"] as const,
  ingredientsReorderCount: ["restaurant", "ingredients", "reorder-count"] as const,
  /**
   * P7.5 — the bill pages' table and area names. Under `grid`, so every
   * floor signal and grid invalidation refreshes it as it refreshed the grid.
   */
  tablePlaces: ["restaurant", "grid", "places"] as const,
  /**
   * P7.6 — the cashier's waiting lane's plates for these bills (sorted ids).
   * Under `invoices` (the floor signal), and `invalidateInvoiceMutation`
   * marks every lane read stale, as it marked each bill it read.
   */
  cashierLane: (invoiceIds: readonly string[]) =>
    ["restaurant", "invoices", "cashier-lane", ...invoiceIds] as const,
  paymentOptions: ["payment-options"] as const,
  kitchenStations: ["restaurant", "kitchen-stations"] as const,
  printingConfig: ["restaurant", "printing", "config"] as const,
  printers: ["restaurant", "printing", "printers"] as const,
  qzSetup: ["restaurant", "printing", "qz-setup"] as const,
  printDevices: ["restaurant", "printing", "devices"] as const,
  printDeviceSession: ["restaurant", "printing", "device", "session"] as const,
  /**
   * One chit. Never folded into the board: it comes from its own endpoint
   * behind its own permission (`RESTAURANT_KITCHEN_PRINT`), and a board refetch
   * every five seconds must not be able to re-pull a chit that is mid-print.
   */
  kitchenTicket: (ticketId: string) =>
    ["restaurant", "kitchen-tickets", ticketId] as const,
  /**
   * The kitchen's 86 list. Its own key, never folded into `menuItems`: that one
   * is the back office's price-carrying list behind a different permission, and
   * a cook's cache must not be filled (or emptied) by it.
   */
  kitchenAvailability: ["restaurant", "kitchen-availability"] as const,
  // Staff & roles live under the seller API (decision D13), so they are keyed
  // as `seller` — a `/manage/staff` invalidation must not sweep the POS caches.
  roles: ["seller", "roles"] as const,
  rolePermissions: ["seller", "roles", "permissions"] as const,
  members: ["seller", "members"] as const,
  invites: ["seller", "invites"] as const,
  /** Team → Members' last-active column (round 5). A restaurant read, so it
   *  is keyed under `restaurant`, beside the seller-API `members` it decorates. */
  teamActivity: ["restaurant", "team", "activity"] as const,
  // Categories and product images are seller-API objects too (D13).
  categories: ["seller", "categories"] as const,
  /** One product's detail — where a menu item's image gallery is read from. */
  sellerProduct: (productId: string) =>
    ["seller", "product", productId] as const,
  /**
   * One ingredient's two append-only ledgers, keyed separately from
   * `ingredients` for the same reason `reconcile` is: they come from their own
   * endpoints and are read on demand, so a purchase invalidating the LIST must
   * not quietly refetch (or discard) a history the owner is reading.
   *
   * The purchases key is shared by the history modal and the edit modal — the
   * edit modal reads it for exactly one bit, "does any purchase exist", which
   * is the only part of the server's unit lock a client can observe.
   */
  /**
   * The store's suppliers (B45, unit 5). One key for the whole list — the
   * picker on the purchase form and the suppliers screen read the same rows,
   * so a supplier added in one is in the other without a second fetch.
   */
  suppliers: ["restaurant", "suppliers"] as const,
  /**
   * Minimal purchasing (2026-10-02). The open orders and the reorder
   * suggestions are separate keys: placing or receiving an order invalidates
   * both, while a supplier's own delivery list is keyed under `suppliers` so
   * invalidating the supplier list refreshes it too.
   */
  ingredientOrders: ["restaurant", "ingredient-orders"] as const,
  reorderSuggestions: ["restaurant", "ingredient-orders", "suggestions"] as const,
  supplierDeliveries: (supplierId: string) =>
    ["restaurant", "suppliers", supplierId, "deliveries"] as const,
  /**
   * Receiving a delivery (D147): the screen's two COST-FREE pickers. Their own
   * keys — not `ingredients`/`suppliers`, which carry costs and "You owe" and
   * are read under other grants — so a cashier's screen never shares a cache
   * entry with an owner's cost read.
   */
  deliveryIngredients: ["restaurant", "deliveries", "ingredients"] as const,
  deliverySuppliers: ["restaurant", "deliveries", "suppliers"] as const,
  /**
   * The store's aggregators (F2, unit 4). One key for the whole list — the
   * picker on the till's delivery form and the management screen read the same
   * rows, so a platform added in one is in the other without a second fetch.
   */
  aggregators: ["restaurant", "aggregators"] as const,
  /**
   * P7.2 — the platforms as the till's pickers offer them: the list without
   * its bill count. Under `aggregators`, so an edit or a retire (and the
   * settings signal) refreshes the till's choices with the list.
   */
  aggregatorChoices: ["restaurant", "aggregators", "choices"] as const,
  /**
   * P6b — the platforms' totals over a period, read only when the manager asks.
   * Its own root, not under `aggregators`: an edit or a retire invalidates the
   * list, and must not re-add up money it cannot move (each bill keeps the rate
   * it was opened at).
   */
  aggregatorTotals: (from: string, to: string) =>
    ["restaurant", "aggregator-totals", from, to] as const,
  ingredientPurchases: (ingredientId: string) =>
    ["restaurant", "ingredients", ingredientId, "purchases"] as const,
  ingredientAdjustments: (ingredientId: string) =>
    ["restaurant", "ingredients", ingredientId, "adjustments"] as const,
  /**
   * The depletion EVENTS behind the sums (the record drawer's "consumed in
   * preparation" view). Its own key under the ingredient, for the reason the
   * two ledgers above are separate.
   */
  ingredientDepletions: (ingredientId: string) =>
    ["restaurant", "ingredients", ingredientId, "depletions"] as const,
  /**
   * The item card (D135), keyed by its range — two ranges never share an
   * entry. `itemCardAll` is the PREFIX every range of one ingredient's card
   * sits under, so a purchase, an adjustment or a return can refresh whatever
   * range is open without knowing it. Not swept by `invalidateRestaurantStock`
   * (that one is `exact` on `ingredients`).
   */
  itemCardAll: (ingredientId: string) =>
    ["restaurant", "ingredients", ingredientId, "card"] as const,
  itemCard: (ingredientId: string, from: string, to: string) =>
    ["restaurant", "ingredients", ingredientId, "card", from, to] as const,
  /** The plates behind one grouped card row — keyed by its day, kind and run (PA-6). */
  itemCardIssues: (ingredientId: string, date: string, kind: string, run?: number) =>
    [
      "restaurant",
      "ingredients",
      ingredientId,
      "card",
      "issues",
      date,
      kind,
      run ?? "day",
    ] as const,
  /**
   * Stocktake (D143). `stocktakes` is the prefix: opening, counting, posting
   * or cancelling invalidates the open count and every list under it.
   */
  stocktakes: ["restaurant", "stocktakes"] as const,
  stocktakeCurrent: ["restaurant", "stocktakes", "current"] as const,
  stocktakeList: (from: string, to: string) =>
    ["restaurant", "stocktakes", "list", from, to] as const,
  stocktake: (id: string) => ["restaurant", "stocktakes", "id", id] as const,
  /**
   * One ingredient's 86 log (B18). Its own key, never folded into
   * `ingredients`, for the reason the purchases and adjustments keys beside it
   * are separate: it is a capped, unbounded list fetched only when a modal
   * opens, and hanging it off the list read would refetch every log on every
   * list invalidation.
   */
  ingredientAvailabilityHistory: (ingredientId: string) =>
    [
      "restaurant",
      "ingredients",
      ingredientId,
      "availability-history",
    ] as const,
  /**
   * The spent-vs-gained report, keyed by its `from`/`to` range — two different
   * ranges must never share a cache entry, the same reasoning as
   * `invoiceHistory`.
   */
  spendGain: (from: string, to: string, compare?: "previous") =>
    ["restaurant", "reports", "spend-gain", from, to, compare ?? null] as const,
  /** Per-item profitability (S1), keyed by its own `from`/`to` range — same
   *  reasoning as `spendGain`: two different ranges never share a cache entry. */
  itemProfitability: (from: string, to: string) =>
    ["restaurant", "reports", "item-profitability", from, to] as const,
  /** The input side of the VAT return (F8), keyed by range — same reasoning
   *  as `spendGain`: two different ranges never share a cache entry. */
  inputVat: (from: string, to: string) =>
    ["restaurant", "reports", "input-vat", from, to] as const,
  /** Voids & comps by staff (S3), keyed by range. */
  staffExceptions: (from: string, to: string) =>
    ["restaurant", "reports", "staff-exceptions", from, to] as const,
  /** Dayparts (S4), keyed by range. */
  dayparts: (from: string, to: string) =>
    ["restaurant", "reports", "dayparts", from, to] as const,
  /**
   * The cash-drawer shift feature (D23-D29). `shiftCurrent`, `shiftSummary` and
   * `shiftDrawers` are single entries, not per-store: the session already scopes
   * every request to one store.
   *
   * `shiftCurrent` and `shiftSummary` are also per-CASHIER answers since D95 —
   * the server resolves them from the authenticated seller — and that needs no
   * key of its own for the same reason: a session is one seller, and a different
   * login is a different cache.
   */
  /**
   * A bill's diner-submitted payment claims (D44). Per invoice: a claim belongs
   * to one bill and two bills must never share the entry.
   */
  invoicePayments: (invoiceId: string) =>
    ["restaurant", "invoice", invoiceId, "payments"] as const,
  shiftCurrent: ["restaurant", "shift-current"] as const,
  /**
   * The drawer this cashier may still count once more (B117/B3, D99).
   *
   * Its own entry, and invalidated by exactly what changes the answer: a close
   * creates one, a recount consumes it, and opening a fresh drawer does not
   * touch it — the drawer just closed stays recountable while the next one is
   * being run.
   */
  shiftRecountable: ["restaurant", "shift-recountable"] as const,
  /** B117/Q2 — the caller's own filed drawers, for the notes written on them. */
  shiftsMine: ["restaurant", "shifts-mine"] as const,
  shiftSummary: ["restaurant", "shift-summary"] as const,
  /**
   * Who is on which box, and the boxes this store has named (D95, Unit B).
   *
   * Its own entry rather than a field of `shiftCurrent`, because the two are
   * invalidated by different things: `shiftCurrent` changes when THIS cashier
   * opens or closes, and this changes when ANY of them does.
   */
  shiftDrawers: ["restaurant", "shift-drawers"] as const,
  /**
   * This store's staff, for the counterparty picker on a custody movement
   * (Unit E).
   *
   * A team list changes in weeks, not seconds, so it is cached hard and NOT
   * re-read unless its access/staff revision changes. Drawers also change
   * whenever anybody opens or closes a box.
   */
  restaurantStaff: ["restaurant", "staff"] as const,
  /** D156 — the approval sheet's chips (`GET /restaurant/approvers`). */
  approvers: ["restaurant", "approvers"] as const,
  /** A page of closed-shift history, keyed by the full query string — same
   *  reasoning as `invoiceHistory`: two different pages must never share a
   *  cache entry. */
  shifts: (search: string) => ["restaurant", "shifts", search] as const,
  shift: (id: string) => ["restaurant", "shifts", id] as const,
  /**
   * The Z report, keyed by the requested business date — `null` (the "close
   * today" default) is its own key rather than sharing one with an explicit
   * date, since which day "today" is changes without any param on the URL
   * changing.
   */
  dayClose: (businessDate: string | null) =>
    ["restaurant", "day-close", businessDate ?? "default"] as const,
};
